<template>
    <div class="t_content_view">
        <el-page-header @back="goBack" :content="isEdit ? '编辑收集码' : '新建收集码'" />
        <div class="t_form_desc">设置客户需填写的信息与接待坐席，生成链接后即可投放使用</div>

        <div class="t_add_title">
            基础信息
        </div>
        <el-form label-width="140px">
            <el-form-item label="收集码名称：">
                <el-input style="width: 320px" maxlength="20" show-word-limit placeholder="例如：十月体检预约"
                    v-model="form.name" />
            </el-form-item>
            <el-form-item label="企微主体：">
                <el-select style="width: 320px" v-model="form.corp_id" placeholder="请选择企微主体" @change="corpChange">
                    <el-option v-for="(item, index) in corpList.data" :key="index" :label="item.corp_name"
                        :value="item.id" />
                </el-select>
            </el-form-item>
            <el-form-item label="接待坐席：">
                <el-select style="width: 480px" v-model="form.seat_ids" multiple filterable collapse-tags
                    placeholder="请选择接待坐席">
                    <el-option v-for="(item, index) in seatList.data" :key="index" :label="item.name"
                        :value="item.id" />
                </el-select>
                <div class="t_hint">客户填写后随机添加所选坐席之一为微信好友，可多选</div>
            </el-form-item>
        </el-form>

        <div class="t_add_title">
            客户需填写<span class="t_hint" style="margin-left: 10px;">至少选择 1 项</span>
        </div>
        <div class="t_field_box">
            <div class="t_field_row" v-for="(item, index) in fieldList.data" :key="index">
                <el-checkbox :model-value="isFieldChecked(item.key)" @change="toggleField(item.key, $event)">
                    {{ item.label }}
                </el-checkbox>
                <el-button size="small" :disabled="!isFieldChecked(item.key)"
                    :color="isRequired(item.key) ? '#4F46E5' : ''"
                    :plain="!isRequired(item.key)" @click="toggleRequired(item.key)">
                    {{ isRequired(item.key) ? '必填' : '选填' }}
                </el-button>
            </div>
            <div class="t_hint">勾选需要客户填写的字段，右侧可逐项设置为必填或选填</div>
        </div>

        <div class="t_add_title">
            预设标签与有效期
        </div>
        <el-form label-width="140px">
            <el-form-item label="预设标签：">
                <div class="t_tag_box">
                    <div class="t_tag_group" v-for="(group, index) in tagList.data" :key="index">
                        <span class="t_tag_group_name">{{ group.cate_name }}</span>
                        <el-check-tag v-for="(tag, tagIndex) in group.tags" :key="tagIndex" class="t_tag_item"
                            :checked="tagIds.indexOf(tag.id) != -1" @change="toggleTag(tag.id)">
                            {{ tag.tag_name }}
                        </el-check-tag>
                    </div>
                    <span v-if="tagList.data.length == 0" class="t_hint">暂无可用标签</span>
                </div>
                <div class="t_hint">客户添加好友成功后自动打上所选标签，标签取自「标签管理」</div>
            </el-form-item>
            <el-form-item label="有效期：">
                <el-radio-group v-model="form.valid_days">
                    <el-radio :label="7">7 天</el-radio>
                    <el-radio :label="30">30 天</el-radio>
                </el-radio-group>
                <div class="t_hint">预计 {{ expireText }} 到期。到期后系统自动删除企微活码，已收集数据不受影响</div>
            </el-form-item>
        </el-form>

        <div style="margin-top: 20px">
            <el-button color="#4F46E5" @click="save" type="primary" :loading="saving">保存</el-button>
            <el-button @click="goBack">取消</el-button>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { newinfodetail, newinfooptions, newinfocreate, newinfoupdate, newgettaglist, newgetcomppeo } from '@/assets/js/common.js'

const route = useRoute()
const router = useRouter()

// 当前是否编辑
const codeId = ref(0)
const isEdit = computed(() => codeId.value > 0)
const saving = ref(false)

// 表单数据
const form = reactive({
    name: '',
    corp_id: '',
    seat_ids: [],
    fields: [],
    valid_days: 7,
    status: 1
})

// 创建时间（编辑时用于计算到期时间）
const createdAt = ref('')

// 企微主体 / 坐席 / 字段
const corpList = reactive({
    data: []
})
const seatList = reactive({
    data: []
})
const fieldList = reactive({
    data: []
})

// 平台标签
const tagList = reactive({
    data: []
})
const tagIds = ref([])

const goBack = () => {
    router.push({ path: '/navtkyl/infocollectlist' })
}

// 获取表单选项（主体/字段）
const getoptions = (corpId) => {
    newinfooptions({ corp_id: corpId || '' }).then((response) => {
        if (response.data.msg.code == 0) {
            corpList.data = response.data.data.corps
            fieldList.data = response.data.data.fields
            getseats(corpId)
        } else {
            ElMessage({ type: 'warning', message: response.data.msg.info })
        }
    })
}

// 接待坐席复用渠道活码/专属加人码的接口，只返回已绑定设备的坐席
const getseats = (corpId) => {
    seatList.data = []
    if (!corpId) return
    newgetcomppeo().then((response) => {
        if (response.data.msg.code == 0) {
            const corp = (response.data.data || []).find((item) => Number(item.corp_id) === Number(corpId))
            const devices = corp && corp.device ? corp.device : []
            const seats = []
            devices.forEach((device) => {
                if (!seats.some((seat) => seat.id === Number(device.id))) {
                    seats.push({ id: Number(device.id), name: device.name })
                }
            })
            seatList.data = seats
        }
    })
}

// 切换企微主体：清空已选坐席并重新加载
const corpChange = () => {
    form.seat_ids = []
    seatList.data = []
    getoptions(form.corp_id)
}

// 获取详情
const getdetail = () => {
    newinfodetail({ id: codeId.value }).then((response) => {
        if (response.data.msg.code == 0) {
            const data = response.data.data
            form.name = data.name
            form.corp_id = data.corp_id
            form.seat_ids = data.seat_ids || []
            form.fields = (data.fields || []).map((item) => {
                return { key: item.key, required: !!item.required }
            })
            tagIds.value = data.tag_ids || []
            form.valid_days = data.valid_days
            form.status = data.status
            createdAt.value = data.created_at || ''
            getoptions(form.corp_id)
        } else {
            ElMessage({ type: 'warning', message: response.data.msg.info })
            goBack()
        }
    })
}

// 获取平台标签
const gettags = () => {
    newgettaglist().then((response) => {
        if (response.data.msg.code == 0) {
            tagList.data = response.data.data
        }
    })
}

const isFieldChecked = (key) => {
    return form.fields.some((item) => item.key === key)
}
const toggleField = (key, checked) => {
    if (checked) {
        if (!isFieldChecked(key)) {
            form.fields.push({ key: key, required: false })
        }
    } else {
        form.fields = form.fields.filter((item) => item.key !== key)
    }
}
const isRequired = (key) => {
    const item = form.fields.find((field) => field.key === key)
    return !!item && item.required
}
const toggleRequired = (key) => {
    const item = form.fields.find((field) => field.key === key)
    if (item) {
        item.required = !item.required
    }
}

const toggleTag = (id) => {
    const index = tagIds.value.indexOf(id)
    if (index == -1) {
        tagIds.value.push(id)
    } else {
        tagIds.value.splice(index, 1)
    }
}

// 预计到期时间：编辑按创建时间 + 有效期，新建按当前时间 + 有效期
const expireText = computed(() => {
    const base = createdAt.value ? new Date(createdAt.value.replace(/-/g, '/')) : new Date()
    const expire = new Date(base.getTime() + form.valid_days * 86400000)
    const pad = (val) => (val < 10 ? '0' + val : val)
    return expire.getFullYear() + '-' + pad(expire.getMonth() + 1) + '-' + pad(expire.getDate())
})

const save = () => {
    if (!form.name.trim()) {
        ElMessage({ type: 'warning', message: '请填写收集码名称' })
        return
    }
    if (!form.corp_id) {
        ElMessage({ type: 'warning', message: '请选择企微主体' })
        return
    }
    if (form.seat_ids.length == 0) {
        ElMessage({ type: 'warning', message: '请至少选择一个接待坐席' })
        return
    }
    if (form.fields.length == 0) {
        ElMessage({ type: 'warning', message: '请至少选择一个收集字段' })
        return
    }

    const payload = {
        name: form.name.trim(),
        corp_id: form.corp_id,
        seat_ids: form.seat_ids,
        fields: form.fields,
        tag_ids: tagIds.value,
        valid_days: form.valid_days,
        status: form.status
    }

    saving.value = true
    const request = isEdit.value
        ? newinfoupdate(Object.assign({ id: codeId.value }, payload))
        : newinfocreate(payload)
    request.then((response) => {
        if (response.data.msg.code == 0) {
            ElMessage({ type: 'success', message: '保存成功！' })
            goBack()
        } else {
            ElMessage({ type: 'warning', message: response.data.msg.info })
        }
    }).finally(() => {
        saving.value = false
    })
}

onMounted(() => {
    if (route.query.id != undefined && route.query.id != '') {
        codeId.value = Number(route.query.id)
        getdetail()
    } else {
        getoptions(0)
    }
    gettags()
})
</script>

<style scoped>
.t_form_desc {
    font-size: 13px;
    color: #64748B;
    margin-top: 10px;
}

.t_hint {
    font-size: 12px;
    color: #94A3B8;
    line-height: 1.6;
}

.t_field_box {
    padding: 4px 0 0 20px;
}

.t_field_row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
}

.t_field_row .el-checkbox {
    width: 110px;
    margin-right: 0;
}

.t_tag_box {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.t_tag_group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.t_tag_group_name {
    width: 90px;
    font-size: 13px;
    color: #64748B;
    flex-shrink: 0;
}

.t_tag_item {
    margin-right: 0;
}
</style>
